import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditableMarkdownField, EditableText, useSectionEditing } from '@kevlarcon/pages';
import { StateProperties, fetchStateMap, registeredStates } from '../../api/states';
import { useStateMap } from '../../hooks/useStateMap';
import StateMap from '../StateMap';

type StatesProps = {
  eyebrow?: string;
  heading: string;
  lede?: string;
  /** Legend labels, one per tier the map draws. Unset ones fall back to
   * LEGEND_DEFAULTS; they also name the tier in the hover callout. */
  legendRegistered?: string;
  legendOptedIn?: string;
  legendNotOptedIn?: string;
};

const LEGEND_DEFAULTS = {
  registered: 'Where we work now',
  optedIn: 'Opted in',
  notOptedIn: 'Not opted in',
};

/**
 * The coverage map.
 *
 * The states drawn here are chosen by the server: the bounding box of everywhere
 * this organization is a registered SGO, filled in with every state that box
 * touches. A state is clickable exactly when it currently has a page — driven
 * by `has_page` (see `state_config.visibility`/`Organization.state_page_visibility`),
 * not by `sgo_registered` directly — and routes to its CMS-editable page at
 * `/pages/state/<abbr>/` (see `tenant_site.page_templates.StatePageTemplate`).
 * The rest are there to make the footprint legible and are inert. Only the
 * surrounding eyebrow/heading/lede are admin-editable (see
 * `tenant_site/section_types.py`'s `STATES_MAP`) — the map itself is live
 * `state_config` data, not a field.
 */
export default function States({
  eyebrow, heading, lede, legendRegistered, legendOptedIn, legendNotOptedIn,
}: StatesProps) {
  const { active, background } = useSectionEditing();
  const navigate = useNavigate();
  const request = useStateMap(fetchStateMap, []);
  const [hovered, setHovered] = useState<StateProperties | null>(null);

  const handleSelect = useCallback(
    (code: string) => navigate(`/pages/state/${code.toLowerCase()}`),
    [navigate],
  );

  const registeredLabel = legendRegistered || LEGEND_DEFAULTS.registered;
  const optedInLabel = legendOptedIn || LEGEND_DEFAULTS.optedIn;
  const notOptedInLabel = legendNotOptedIn || LEGEND_DEFAULTS.notOptedIn;

  const registered = request.status === 'ready' ? registeredStates(request.data) : [];
  // Only states the organization has actually opted into are "in development".
  // The rest of the roster is drawn for context, and claimed as nothing.
  const inDevelopment =
    request.status === 'ready'
      ? request.data.features.filter((f) => f.properties.opted_in && !f.properties.sgo_registered)
      : [];

  return (
    <section className="states section">
      <div className="wrap">
        <div className="head">
          {(eyebrow || active) && (
            <EditableText as="div" className="eyebrow" kind="text" value={eyebrow ?? ''} path={['eyebrow']} />
          )}
          <EditableText as="h2" kind="richtext" value={heading} path={['heading']} />
          {(lede || active) && (
            <EditableMarkdownField value={lede ?? ''} path={['lede']} />
          )}
        </div>

        <div className="state-map-wrap">
          {request.status === 'ready' && request.data.bbox ? (
            <StateMap
              data={request.data}
              onHover={setHovered}
              onSelect={handleSelect}
              testId="states-coverage-map"
              ariaLabel="Map of the states where this organization is a registered scholarship granting organization"
              background={background}
            />
          ) : (
            <div className="state-map-canvas state-map-placeholder" data-testid="states-map-placeholder">
              <span>
                {request.status === 'error'
                  ? 'The coverage map is unavailable right now.'
                  : request.status === 'ready'
                    ? 'Coverage is being finalized for the coming session.'
                    : 'Loading coverage…'}
              </span>
            </div>
          )}

          <div className="map-callout" aria-live="polite">
            {hovered ? (
              <>
                <div className="map-callout-code">{hovered.abbreviated_name}</div>
                {hovered.sgo_registered ? (
                  <>
                    <div className="map-callout-status">{registeredLabel}</div>
                    <div className="map-callout-meta">
                      {hovered.full_name}
                      {hovered.has_page ? ' — state tax-credit; select for details' : ' — state tax-credit'}
                    </div>
                  </>
                ) : hovered.opted_in ? (
                  <>
                    <div className="map-callout-status">{optedInLabel}</div>
                    <div className="map-callout-meta">
                      {hovered.full_name}{hovered.has_page ? ' — select for details' : ''}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="map-callout-status">{notOptedInLabel}</div>
                    <div className="map-callout-meta">{hovered.full_name}</div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="map-callout-code muted">Hover a state</div>
                <div className="map-callout-status">
                  {registered.length} {registeredLabel.toLowerCase()} · {inDevelopment.length} {optedInLabel.toLowerCase()}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="state-legend">
          <span>
            <span className="swatch active"></span>
            <EditableText as="span" kind="text" value={legendRegistered ?? LEGEND_DEFAULTS.registered} path={['legendRegistered']} />
          </span>
          <span>
            <span className="swatch coming"></span>
            <EditableText as="span" kind="text" value={legendOptedIn ?? LEGEND_DEFAULTS.optedIn} path={['legendOptedIn']} />
          </span>
          <span>
            <span className="swatch neighbor"></span>
            <EditableText as="span" kind="text" value={legendNotOptedIn ?? LEGEND_DEFAULTS.notOptedIn} path={['legendNotOptedIn']} />
          </span>
        </div>
      </div>
    </section>
  );
}
